import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { cn } from "@nexus/ui";
import type {
  CreateUserKpiBody,
  UpdateUserKpiBody,
  UserKpiEntry,
} from "@nexus/contracts";
import { reorderUserKpiNames } from "../lib/user-kpi-model";
import { KpiSettingsDialog, type KpiSettingsDialogValue } from "./KpiSettingsDialog";
import { UserKpiBox, type UserKpiBoxLayout, type UserKpiTone } from "./UserKpiBox";

/** Per-KPI display values the owning feature computes (badge logic stays theirs). */
export interface UserKpiCellValue {
  value: string;
  badgeText: string | null;
  tone: UserKpiTone;
}

export interface UserKpiGridProps {
  entries: UserKpiEntry[];
  /** Keyed by `UserKpiEntry.name`. A missing entry renders the empty placeholder. */
  valuesByName: Record<string, UserKpiCellValue | undefined>;
  revealHidden?: boolean;
  isLoading?: boolean;
  emptyValue?: string;
  className?: string;
  boxLayout?: UserKpiBoxLayout;
  onReorder?: (names: string[]) => void;
  onCreate?: (body: CreateUserKpiBody) => void;
  onUpdate?: (id: number, body: UpdateUserKpiBody) => void;
  isSubmitting?: boolean;
  onToggleHidden?: (entry: UserKpiEntry) => void;
}

export function UserKpiGrid({
  entries,
  valuesByName,
  revealHidden = false,
  isLoading = false,
  emptyValue = "—",
  className,
  boxLayout = "default",
  onReorder,
  onCreate,
  onUpdate,
  isSubmitting = false,
  onToggleHidden,
}: UserKpiGridProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserKpiEntry | null>(null);

  // A click that does not travel 5px is a click, not a drag — otherwise the
  // PointerSensor swallows the edit and hide buttons inside each box.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const rendered = useMemo(
    () => (revealHidden ? entries : entries.filter((entry) => !entry.hidden)),
    [entries, revealHidden],
  );
  const sortableIds = useMemo(() => rendered.map((entry) => entry.name), [rendered]);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorder) return;
    onReorder(reorderUserKpiNames(entries, String(active.id), String(over.id)));
  }

  function handleSubmit(value: KpiSettingsDialogValue) {
    if (editing && onUpdate) {
      onUpdate(editing.id, {
        displayName: value.displayName,
        formula: value.formula,
        format: value.format,
        polarity: value.polarity,
        decimals: value.decimals,
        colorToken: value.colorToken,
      });
    } else if (onCreate) {
      onCreate({
        displayName: value.displayName,
        formula: value.formula,
        format: value.format,
        polarity: value.polarity,
        decimals: value.decimals,
        colorToken: value.colorToken,
        hidden: false,
        shareWithOrg: value.shareWithOrg,
      });
    }
    setDialogOpen(false);
    setEditing(null);
  }

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
          <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-3", className)}>
            {rendered.map((entry) => {
              const cell = valuesByName[entry.name];
              return (
                <UserKpiBox
                  key={entry.name}
                  entry={entry}
                  layout={boxLayout}
                  value={cell?.value ?? emptyValue}
                  badgeText={cell?.badgeText ?? null}
                  tone={cell?.tone ?? "neutral"}
                  isLoading={isLoading}
                  draggable={!!onReorder}
                  onEdit={
                    onUpdate
                      ? (target) => {
                          setEditing(target);
                          setDialogOpen(true);
                        }
                      : undefined
                  }
                  onToggleHidden={onToggleHidden}
                />
              );
            })}

            {onCreate && (
              <button
                type="button"
                className="flex min-h-[72px] items-center justify-center gap-1.5 rounded-lg border border-dashed border-border text-xs font-medium text-muted-foreground hover:border-ring hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="size-3.5" aria-hidden />
                {t("kpiSettings.addBox", "Add KPI")}
              </button>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {(onCreate || onUpdate) && (
        <KpiSettingsDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) setEditing(null);
          }}
          mode={editing ? "edit" : "create"}
          scope="user"
          isSubmitting={isSubmitting}
          initial={
            editing
              ? {
                  displayName: editing.displayName,
                  formula: editing.formula ?? editing.name,
                  format: editing.format,
                  polarity: editing.polarity,
                  decimals: editing.decimals,
                  colorToken: editing.colorToken,
                }
              : undefined
          }
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}
